/**
 * SIM-1724 — Skills-tab-local staged-edit state for the atomic save UX. Creates, updates and
 * deletes are staged here, scoped to the tab: it deliberately does NOT touch the shared
 * `useDirtyForm`. State is `edits` (overrides on an existing row), `creates` (drafts keyed by
 * tempId), `deletes` and `expanded`. `mergeRows` overlays the server list; `toBatch` projects it.
 */
import type { AgentSkill } from "./types";

export interface SkillFields {
  name: string;
  description: string;
  body: string;
}

/** A row as rendered: either backed by a server row or a local draft. */
export interface DraftRow extends SkillFields {
  /** Stable render key. For existing rows this is the server id; for creates the tempId. */
  key: string;
  /** Server id when this row exists on the server, else null (a create). */
  id: string | null;
  status: "unchanged" | "edited" | "created" | "deleted";
}

export interface SkillsDraftState {
  edits: Record<string, SkillFields>;
  creates: Array<{ tempId: string } & SkillFields>;
  deletes: string[];
  expanded: string[];
}

export const initialDraftState: SkillsDraftState = {
  edits: {},
  creates: [],
  deletes: [],
  expanded: [],
};

export type SkillsDraftAction =
  | { type: "startCreate"; tempId: string }
  | { type: "editCreate"; tempId: string; patch: Partial<SkillFields> }
  | { type: "removeCreate"; tempId: string }
  | { type: "editExisting"; id: string; base: SkillFields; patch: Partial<SkillFields> }
  | { type: "markDelete"; id: string }
  | { type: "undoDelete"; id: string }
  | { type: "toggleExpanded"; key: string }
  | { type: "collapse"; key: string }
  | { type: "reset" };

function toggle(list: string[], key: string): string[] {
  return list.includes(key) ? list.filter((k) => k !== key) : [...list, key];
}

const EMPTY_FIELDS: SkillFields = { name: "", description: "", body: "" };

export function skillsDraftReducer(
  state: SkillsDraftState,
  action: SkillsDraftAction,
): SkillsDraftState {
  switch (action.type) {
    case "startCreate":
      return {
        ...state,
        creates: [{ tempId: action.tempId, ...EMPTY_FIELDS }, ...state.creates],
        expanded: [action.tempId, ...state.expanded],
      };
    case "editCreate":
      return {
        ...state,
        creates: state.creates.map((c) =>
          c.tempId === action.tempId ? { ...c, ...action.patch } : c,
        ),
      };
    case "removeCreate":
      return {
        ...state,
        creates: state.creates.filter((c) => c.tempId !== action.tempId),
        expanded: state.expanded.filter((k) => k !== action.tempId),
      };
    case "editExisting": {
      const current = state.edits[action.id] ?? action.base;
      return { ...state, edits: { ...state.edits, [action.id]: { ...current, ...action.patch } } };
    }
    case "markDelete":
      return {
        ...state,
        deletes: state.deletes.includes(action.id) ? state.deletes : [...state.deletes, action.id],
        expanded: state.expanded.filter((k) => k !== action.id),
      };
    case "undoDelete":
      return { ...state, deletes: state.deletes.filter((id) => id !== action.id) };
    case "toggleExpanded":
      return { ...state, expanded: toggle(state.expanded, action.key) };
    case "collapse":
      return { ...state, expanded: state.expanded.filter((k) => k !== action.key) };
    case "reset":
      return initialDraftState;
    default:
      return state;
  }
}

function existingStatus(skill: AgentSkill, state: SkillsDraftState): DraftRow["status"] {
  if (state.deletes.includes(skill.id)) return "deleted";
  return state.edits[skill.id] ? "edited" : "unchanged";
}

/** Overlay staged state onto the server list to produce the rendered rows. */
export function mergeRows(server: AgentSkill[], state: SkillsDraftState): DraftRow[] {
  const created: DraftRow[] = state.creates.map((c) => ({
    key: c.tempId,
    id: null,
    name: c.name,
    description: c.description,
    body: c.body,
    status: "created",
  }));
  const existing: DraftRow[] = server.map((skill) => {
    const override = state.edits[skill.id];
    return {
      key: skill.id,
      id: skill.id,
      name: override?.name ?? skill.name,
      description: override?.description ?? skill.description,
      body: override?.body ?? skill.body,
      status: existingStatus(skill, state),
    };
  });
  return [...created, ...existing];
}

export function hasStagedChanges(state: SkillsDraftState): boolean {
  return (
    state.creates.length > 0 || state.deletes.length > 0 || Object.keys(state.edits).length > 0
  );
}

export interface SkillsBatch {
  creates: Array<SkillFields>;
  updates: Array<{ id: string } & SkillFields>;
  deletes: string[];
}

/** Project the staged state into the contract's skills delta. */
export function toBatch(state: SkillsDraftState): SkillsBatch {
  return {
    creates: state.creates.map(({ name, description, body }) => ({ name, description, body })),
    // Skip edits to rows that are also pending-delete — the delete wins.
    updates: Object.entries(state.edits)
      .filter(([id]) => !state.deletes.includes(id))
      .map(([id, f]) => ({ id, name: f.name, description: f.description, body: f.body })),
    deletes: state.deletes,
  };
}
